import type { CodeSpec, InterviewMode, QuestionKind } from "@talenthop/shared";
import { DEFAULT_INTERVIEW_LANGUAGE, OPTION_LETTERS, formatStrictResume, interviewLanguageOf } from "@talenthop/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, MicOff, RotateCcw } from "lucide-react";
import type { ReactElement, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Arrow, Check, Info, Video, X as XIcon } from "../../components/icons.js";
import { api, BASE_URL } from "../../lib/api.js";
import {
  useAzureAshaSpeech,
  useAzureVoiceInput,
  useSpeechSession,
} from "./azureSpeech.js";
import { IDLE_NOTICE, nextCameraNotice, type CameraNoticeState } from "./cameraNoticeView.js";
import type { AudioCue } from "./CandidateBackground.js";
import { ackFor, pickAshaVoice, spokenFor, webSpeechLangFor } from "./interviewLanguage.js";
import { progressView, type ProgressPayload } from "./interviewProgress.js";
import { useBackgroundBlur } from "./useBackgroundBlur.js";
import { useFaceMesh } from "./useFaceMesh.js";
import { useMicLevel } from "./useMicLevel.js";
import { useProctoring } from "./useProctoring.js";
import { useRecorder } from "./useRecorder.js";
import { useSpeechUsageReporter } from "./speechUsage.js";
import { RequestHuman } from "./RequestHuman.js";
import {
  AZURE_TAIL_GUARD_MS,
  TTS_TAIL_GUARD_MS,
  guardAfterSpeech,
  isFatalRecognitionError,
  planAfterRecognitionEnd,
  restartDelayMs,
  shouldAcceptResult,
  shouldGiveUp,
  shouldListen,
  type AnswerMode,
} from "./voiceGate.js";

/**
 * Candidate · interview step — voice-first. "Asha" (an animated orb, NOT a human face)
 * SPEAKS each question via window.speechSynthesis; the candidate answers by VOICE via
 * SpeechRecognition with a live transcript. Question text is hidden by default; Repeat /
 * Rephrase / Show-text / Type-instead controls are always available. code_reading questions
 * render a snippet to explain aloud; code questions keep the editor. Everything degrades
 * gracefully when the Web Speech API is unavailable — the candidate is never trapped.
 * No scores or results are ever shown here.
 */

interface StateQuestion {
  id: string;
  seq: number;
  kind: QuestionKind;
  prompt: string;
  codeSpec: CodeSpec | null;
  snippet?: string | null; // code_reading: the code to read + explain aloud
  /** Snippet choice (#78): 2-4 statements about the snippet — pick one, then explain aloud.
   *  The server never sends which is correct; the pick is checked at submission. */
  options?: string[] | null;
  /** WS4: the prompt rendered into the session language — what Asha speaks and the screen shows.
   *  Null/absent = speak `prompt` (English): an English session, a rendering that failed (the API
   *  chose English over silence), or an API that predates migration 0078. `prompt` stays English
   *  so the judge and the report compare like with like. */
  promptLocalized?: string | null;
}
interface InterviewStateResponse {
  /** `language` (WS4) is the interview_sessions row's own copy of the consent choice — the value
   *  the API minted the speech token and rendered the prompts from, so it beats the invite prop. */
  session: { status?: string; startedAt?: string | null; language?: string } | null;
  progress: ProgressPayload;
  question: StateQuestion | null;
  /** Top-level copy of the session language (the API sends both). */
  language?: string;
}
/** POST …/interview/answer already returns the NEXT question and the fresh progress. We used to
 *  throw that away and wait for a refetch, which is why the counter appeared to lag a question. */
interface AnswerResponse {
  done?: boolean;
  question?: StateQuestion | null;
  progress?: ProgressPayload;
  codeResult?: unknown;
}

/* `code_reading` is the stored kind for EVERY critique artefact, not only code — since migration
   0057 it is far more often a handover note, a job advertisement or a lesson plan (the `artefact`
   family in EXERCISE_QUESTION_KIND). This label is the string the CANDIDATE reads, and "Read the
   code" over a nurse's handover note is the rate-limiter question in miniature. The snippet pane's
   own heading below was already neutral for exactly this reason; this one was not. */
const KIND_LABEL: Record<QuestionKind, string> = {
  behavioral: "Behavioral",
  // "Practical", not "Technical" — same reasoning as the code_reading label above: this is the
  // string the CANDIDATE reads, and a nurse's practical question is not "Technical" (#18).
  technical: "Practical",
  code: "Code",
  code_reading: "Read and explain",
  adaptive_challenge: "Live challenge",
};

// ---- local inline icons (candidate-owned; keeps the shared icon set untouched) ----
type IconProps = { className?: string };
const ico = (path: ReactElement): ((p: IconProps) => ReactElement) =>
  function Icon({ className }: IconProps) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={className} aria-hidden="true">
        {path}
      </svg>
    );
  };
const Mic = ico(
  <>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0014 0M12 18v3" />
  </>,
);
const Stop = ico(<rect x="6" y="6" width="12" height="12" rx="2" />);
const Repeat = ico(<path d="M17 2l4 4-4 4M7 22l-4-4 4-4M21 6H8a5 5 0 00-5 5M3 18h13a5 5 0 005-5" />);
const Wand = ico(<path d="M5 21l10-10M14 4l1.5 3L19 8.5 16 10l-1 3-1.5-3L10 8.5 13 7zM19 15l.7 1.5L21 17l-1.3.5L19 19l-.7-1.5L17 17l1.3-.5z" />);
const Eye = ico(
  <>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
const Keyboard = ico(
  <>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
  </>,
);

// Per-question typed-draft persistence so a mid-answer refresh doesn't lose typed work.
// (Voice transcripts are live/ephemeral and intentionally not persisted.) The "pick" field (#78)
// keeps a snippet-choice selection across a refresh too — a pick is locked once made, so losing
// it to a reload would let the candidate quietly re-pick.
/** How long after the last recognised words the gaze window stays open. Long enough that a
 *  breath or a glance at the question doesn't blink it shut mid-sentence; short enough that a
 *  silent thinking spell goes genuinely unmeasured, as promised at consent. */
const SPEAKING_HOLD_MS = 6_000;

const draftKey = (questionId: string, field: "answer" | "code" | "pick"): string => `th_draft_${field}_${questionId}`;
const readDraft = (questionId: string, field: "answer" | "code" | "pick"): string | null => {
  try {
    return sessionStorage.getItem(draftKey(questionId, field));
  } catch {
    return null;
  }
};
const writeDraft = (questionId: string, field: "answer" | "code" | "pick", value: string): void => {
  try {
    sessionStorage.setItem(draftKey(questionId, field), value);
  } catch {
    /* best-effort */
  }
};
const clearDraft = (questionId: string): void => {
  try {
    sessionStorage.removeItem(draftKey(questionId, "answer"));
    sessionStorage.removeItem(draftKey(questionId, "code"));
    sessionStorage.removeItem(draftKey(questionId, "pick"));
  } catch {
    /* no-op */
  }
};

// ---- minimal Web Speech typings (SpeechRecognition is not in lib.dom) ----
interface RecAlternative {
  transcript: string;
  confidence?: number;
}
interface RecResult {
  0: RecAlternative;
  isFinal: boolean;
  length: number;
}
interface RecResultList {
  length: number;
  [index: number]: RecResult;
}
interface RecEvent {
  resultIndex: number;
  results: RecResultList;
}
/** SpeechRecognition's error event. The `error` CODE is the whole point: `not-allowed` means the
 *  mic is gone for good, `no-speech` means the candidate simply paused. Retrying the first forever
 *  is how a candidate ends up re-prompted in a loop. */
interface RecErrorEvent {
  error?: string;
}
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: RecEvent) => void) | null;
  onerror: ((e: RecErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type RecognitionCtor = new () => RecognitionLike;
function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Pick ONE stable Asha voice for the language (interviewLanguage.ts decides; this only reads the
 *  device's list), remembered per language so every utterance uses the SAME voice — fixes the
 *  voice flipping female↔male between questions. */
function pickAshaVoiceFor(language: string): SpeechSynthesisVoice | null {
  return pickAshaVoice(window.speechSynthesis.getVoices(), language);
}

/**
 * iOS Safari (and some mobile browsers) refuse speechSynthesis.speak() unless it was first invoked
 * inside a user gesture — otherwise Asha's first question plays SILENTLY on a phone. Call this from a
 * real tap (the check-in "Start interview" button) to "warm up" the global speech engine so every
 * later speak() produces sound. Safe/no-op where unsupported. */
export function unlockSpeech(): void {
  try {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    window.speechSynthesis.speak(u);
  } catch {
    /* no-op */
  }
}

/** Poll cadence for the speech watchdog below — fine enough to hand the turn back promptly. */
const SPEECH_WATCHDOG_MS = 200;
/** If the engine never reports itself busy within this window, the utterance was silently dropped
 *  (classic iOS Safari pre-gesture behaviour) and the candidate must get their turn back. */
const SPEECH_NEVER_STARTED_MS = 2_500;

/**
 * Text-to-speech — Asha's voice. One pinned female voice, calm (slow) rate.
 *
 * `speaking` is the mic's kill switch, so this hook owes the rest of the screen two guarantees:
 *
 *  1. It is TRUE from the instant we ask the engine to talk — not from `u.onstart`, which lands
 *     tens to hundreds of milliseconds later. That gap is exactly how Asha's own question ended
 *     up typed into "Your spoken answer".
 *  2. It ALWAYS comes back down. A flag stuck true means a candidate who can never answer, so on
 *     top of onend/onerror there is a watchdog that watches the engine itself and a hard cap.
 */
function useAshaSpeech(language: string): {
  supported: boolean;
  speaking: boolean;
  /** The same truth as `speaking`, readable SYNCHRONOUSLY. SpeechRecognition delivers results on a
   *  browser callback that can land before React has re-rendered, so the gate reads this ref. */
  speakingRef: RefObject<boolean>;
  speak: (t: string) => void;
  cancel: () => void;
} {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [speaking, setSpeaking] = useState(false);
  const speakingRef = useRef(false);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);
  // Every utterance gets a generation. A cancelled utterance's `onend` can arrive AFTER the next
  // one has started; without this it would clear the flag and unmute the mic mid-question.
  const genRef = useRef(0);
  const watchdogRef = useRef<number | null>(null);

  const setSpeakingBoth = useCallback((v: boolean) => {
    speakingRef.current = v;
    setSpeaking(v);
  }, []);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current !== null) {
      window.clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const finish = useCallback(
    (gen: number) => {
      if (gen !== genRef.current) return; // stale utterance — not ours to end
      stopWatchdog();
      setSpeakingBoth(false);
    },
    [setSpeakingBoth, stopWatchdog],
  );

  // Voices can load asynchronously; grab (and cache) the pinned voice as soon as they're available.
  // The cache is PER LANGUAGE: it is dropped when the language changes (the session language can
  // land a render after the invite prop) so a Tamil session never keeps the English voice it was
  // pinned to while the state payload was still loading.
  useEffect(() => {
    if (!supported) return;
    voiceRef.current = null;
    const grab = () => {
      if (!voiceRef.current) voiceRef.current = pickAshaVoiceFor(language);
    };
    grab();
    window.speechSynthesis.addEventListener?.("voiceschanged", grab);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", grab);
  }, [supported, language]);

  // iOS audio-unlock fallback: the FIRST tap/pointer anywhere warms up the speech engine, so even if
  // the interview auto-speaks before an explicit gesture, the next question is audible. One-shot.
  useEffect(() => {
    if (!supported) return;
    const unlock = () => {
      unlockSpeech();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("touchend", unlock);
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("touchend", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("touchend", unlock);
    };
  }, [supported]);

  const cancel = useCallback(() => {
    if (!supported) return;
    genRef.current += 1; // orphan the running utterance's handlers before we kill it
    stopWatchdog();
    window.speechSynthesis.cancel();
    setSpeakingBoth(false);
  }, [supported, setSpeakingBoth, stopWatchdog]);

  const speak = useCallback(
    (text: string) => {
      if (!supported || !text.trim()) return;
      const gen = ++genRef.current;
      // Flag UP first, synchronously, THEN talk. Not in u.onstart: the engine can take a few
      // hundred ms to fire it, and for every one of those milliseconds the mic would be live and
      // transcribing Asha into the candidate's answer.
      setSpeakingBoth(true);
      stopWatchdog();
      window.speechSynthesis.cancel(); // clear the queue; its handlers are already orphaned above

      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.85; // calmer, easier to follow than the near-default 0.98
      u.pitch = 1.02;
      // The language tag is set even when a voice is pinned (a pinned voice wins in every engine,
      // so English is unchanged); it matters when the picker found NO voice for the language —
      // the engine may still have a default of its own for the tag, and the alternative is an
      // English voice reading Tamil script.
      // English keeps the pre-WS4 utterance exactly (no lang tag → the platform default voice).
      if (language !== DEFAULT_INTERVIEW_LANGUAGE) u.lang = webSpeechLangFor(language);
      if (!voiceRef.current) voiceRef.current = pickAshaVoiceFor(language);
      if (voiceRef.current) u.voice = voiceRef.current;
      u.onstart = () => {
        if (gen === genRef.current) setSpeakingBoth(true); // reaffirm; the flag is already true
      };
      u.onend = () => finish(gen);
      u.onerror = () => finish(gen); // a refused or interrupted utterance still hands the turn back
      window.speechSynthesis.speak(u);

      // Watchdog. onend is not guaranteed: browsers silently drop utterances (a backgrounded tab,
      // iOS before its first gesture, an engine hiccup) and Chrome has shipped builds where the
      // event never lands. A flag stuck TRUE means a candidate who can never answer — far worse
      // than an echo — so we also watch the engine, and cap the whole thing by utterance length.
      const startedAt = Date.now();
      const words = text.trim().split(/\s+/).length;
      const hardCapMs = Math.min(180_000, 8_000 + words * 900);
      let engineSawIt = false;
      watchdogRef.current = window.setInterval(() => {
        if (gen !== genRef.current) {
          stopWatchdog();
          return;
        }
        const synth = window.speechSynthesis;
        const busy = synth.speaking || synth.pending;
        if (busy) engineSawIt = true;
        const elapsed = Date.now() - startedAt;
        // It ran and is now done (onend never came) · it never reached the engine at all ·
        // or it has outlasted any plausible reading of this text.
        if ((engineSawIt && !busy) || (!engineSawIt && elapsed > SPEECH_NEVER_STARTED_MS) || elapsed > hardCapMs) {
          finish(gen);
        }
      }, SPEECH_WATCHDOG_MS);
    },
    [supported, language, finish, setSpeakingBoth, stopWatchdog],
  );

  useEffect(
    () => () => {
      stopWatchdog();
      if (supported) window.speechSynthesis.cancel();
    },
    [supported, stopWatchdog],
  );
  return { supported, speaking, speakingRef, speak, cancel };
}

interface VoiceInput {
  supported: boolean;
  /** A recogniser is genuinely running right now (reported, never assumed). */
  listening: boolean;
  /** The mic is not coming back — denied, absent, or it refused to stay up. The UI must move the
   *  candidate to typing rather than let them talk into a dead microphone. */
  blocked: boolean;
  finalText: string;
  interim: string;
  transcript: string;
  /** Average browser-reported confidence over final results, 0–1. Null when the browser
   *  doesn't expose confidence (many don't) — never fabricated, just omitted from the UI. */
  confidence: number | null;
  /** Declare INTENT: "should we be listening right now?". Idempotent, so a render-driven effect
   *  can call it every time without ever toggling the recogniser. */
  setIntent: (want: boolean) => void;
  reset: () => void;
}

/**
 * Speech-to-text — the candidate's spoken answer, streamed as a live transcript.
 *
 * The hook owns the RECOGNISER; the caller owns the INTENT. That split is the fix for the
 * flicker: the old version exposed start()/stop() and the caller drove them from an effect that
 * also read `listening`, so every start re-triggered the effect that started it. Here the caller
 * says only "listen / don't listen" and this hook reconciles reality to that, including putting
 * the recogniser back when Chrome ends it on its own — with a backoff, so a denied mic or a
 * hidden tab can never turn into a restart storm.
 *
 * @param acceptResult gate consulted at the instant a result arrives; false → the text is
 *   discarded (it is Asha's voice, not the candidate's).
 * @param language the session language (an INTERVIEW_LANGUAGE_CODES code, coerced upstream) —
 *   the recogniser is told its BCP-47 tag. Read when a recogniser is CREATED: a change re-creates
 *   `startNow`, and the next start/restart picks it up; a live recogniser keeps its tag until the
 *   turn ends, which is right — the language is fixed at consent, never switched mid-answer.
 */
function useVoiceInput(acceptResult: (at: number) => boolean, language: string): VoiceInput {
  const Ctor = recognitionCtor();
  const supported = Ctor !== null;
  const [listening, setListening] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [finalText, setFinalText] = useState("");
  const [interim, setInterim] = useState("");
  const [confidence, setConfidence] = useState<number | null>(null);
  const confSumRef = useRef(0);
  const confCountRef = useRef(0);

  // Latest-value ref: onresult fires from the browser, outside React's render cycle, so the gate
  // it consults has to be readable synchronously at that exact instant — state would be stale.
  const acceptRef = useRef(acceptResult);
  acceptRef.current = acceptResult;

  const recRef = useRef<RecognitionLike | null>(null);
  const genRef = useRef(0); // only the CURRENT recogniser's callbacks may touch state
  const runningRef = useRef(false);
  const intentRef = useRef(false);
  const blockedRef = useRef(false);
  const failuresRef = useRef(0);
  const sessionStartRef = useRef(0);
  const gotResultRef = useRef(false);
  const restartRef = useRef<number | null>(null);
  const startRef = useRef<() => void>(() => undefined);

  const clearRestart = useCallback(() => {
    if (restartRef.current !== null) {
      window.clearTimeout(restartRef.current);
      restartRef.current = null;
    }
  }, []);

  /** Orphan AND abort whatever recogniser is live. Both halves matter: an abandoned recogniser
   *  keeps the mic open and keeps appending into the same transcript — that is how the same
   *  sentence used to land twice. */
  const teardown = useCallback(() => {
    genRef.current += 1;
    const old = recRef.current;
    recRef.current = null;
    runningRef.current = false;
    if (old) {
      old.onresult = null;
      old.onerror = null;
      old.onend = null;
      try {
        old.abort();
      } catch {
        /* already dead */
      }
    }
  }, []);

  const block = useCallback(() => {
    blockedRef.current = true;
    intentRef.current = false;
    setBlocked(true);
  }, []);

  const scheduleRestart = useCallback(
    (delay: number) => {
      clearRestart();
      restartRef.current = window.setTimeout(() => {
        restartRef.current = null;
        startRef.current();
      }, delay);
    },
    [clearRestart],
  );

  const startNow = useCallback(() => {
    if (!Ctor || runningRef.current || blockedRef.current || !intentRef.current) return;
    // A hidden tab starves recognition; restarting into one is the retry storm we are avoiding.
    if (typeof document !== "undefined" && document.hidden) return;
    clearRestart();
    teardown(); // never leave a predecessor holding the mic
    const gen = genRef.current;

    const rec = new Ctor();
    rec.lang = webSpeechLangFor(language);
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e: RecEvent) => {
      if (gen !== genRef.current) return; // superseded recogniser — its words are not the answer
      // Belt and braces. Even if this recogniser should not be alive, anything heard while Asha
      // is speaking (or inside her tail guard) is HER, and the candidate cannot undo text they
      // never said. Drop it here as well as refusing to listen in the first place.
      if (!acceptRef.current(Date.now())) {
        setInterim("");
        return;
      }
      let fin = "";
      let intr = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r) continue;
        if (r.isFinal) {
          fin += r[0].transcript;
          // Chrome reports a real 0–1 confidence on final results; Firefox/Safari report 0
          // (unsupported) — only count values that look like a genuine measurement.
          const c = r[0].confidence;
          if (typeof c === "number" && c > 0) {
            confSumRef.current += c;
            confCountRef.current += 1;
            setConfidence(confSumRef.current / confCountRef.current);
          }
        } else {
          intr += r[0].transcript;
        }
      }
      if (fin || intr) gotResultRef.current = true; // the mic demonstrably works
      if (fin) setFinalText((prev) => `${prev} ${fin}`.trim());
      setInterim(intr);
    };

    rec.onerror = (e: RecErrorEvent) => {
      if (gen !== genRef.current) return;
      // `no-speech`, `aborted`, `network` are transient; a permission refusal is not.
      if (isFatalRecognitionError(e.error)) block();
      // onend always follows — the restart decision is made there, in one place.
    };

    rec.onend = () => {
      if (gen !== genRef.current) return;
      runningRef.current = false;
      setListening(false);
      setInterim("");
      // Chrome ends recognition by itself after a stretch of silence, so SOMETHING has to put it
      // back — that "not capturing" is a candidate talking into a dead mic. The decision (and the
      // loop-avoidance that has to come with it) is pure and unit-tested; this just executes it.
      const plan = planAfterRecognitionEnd({
        intent: intentRef.current,
        previousFailures: failuresRef.current,
        gotResult: gotResultRef.current,
        sessionMs: Date.now() - sessionStartRef.current,
        pageHidden: typeof document !== "undefined" && document.hidden,
      });
      failuresRef.current = plan.failures;
      if (plan.action === "give-up") block();
      else if (plan.action === "restart") scheduleRestart(plan.delayMs);
    };

    recRef.current = rec;
    sessionStartRef.current = Date.now();
    gotResultRef.current = false;
    try {
      rec.start();
      runningRef.current = true;
      setListening(true);
    } catch {
      // Chrome throws InvalidStateError if the previous recogniser has not released the mic yet.
      runningRef.current = false;
      setListening(false);
      failuresRef.current += 1;
      if (shouldGiveUp(failuresRef.current)) block();
      else if (intentRef.current) scheduleRestart(restartDelayMs(failuresRef.current));
    }
  }, [Ctor, language, block, clearRestart, scheduleRestart, teardown]);
  startRef.current = startNow;

  const setIntent = useCallback(
    (want: boolean) => {
      if (blockedRef.current) return; // the UI has already moved them to typing
      if (intentRef.current === want) return; // idempotent — safe to call on every render
      intentRef.current = want;
      if (want) {
        failuresRef.current = 0; // a fresh turn deserves a fresh retry budget
        startNow();
      } else {
        clearRestart();
        teardown();
        setListening(false);
        setInterim("");
      }
    },
    [clearRestart, startNow, teardown],
  );

  const reset = useCallback(() => {
    setFinalText("");
    setInterim("");
    setConfidence(null);
    confSumRef.current = 0;
    confCountRef.current = 0;
  }, []);

  useEffect(
    () => () => {
      clearRestart();
      teardown();
    },
    [clearRestart, teardown],
  );

  const transcript = `${finalText} ${interim}`.trim();
  return useMemo(
    () => ({ supported, listening, blocked, finalText, interim, transcript, confidence, setIntent, reset }),
    [supported, listening, blocked, finalText, interim, transcript, confidence, setIntent, reset],
  );
}

/** Is the tab in the background? Recognition is starved there, so the mic intent goes down with
 *  it (and proctoring already flags the switch-away separately). */
function useDocumentHidden(): boolean {
  const [hidden, setHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  return hidden;
}

/**
 * Live camera self-view. Video-only — SpeechRecognition owns the mic separately, so we don't
 * request audio here and avoid a double prompt. The camera is OPTIONAL: if it's denied or the
 * device has none, the interview proceeds unchanged (answers are what's assessed, never video).
 * The stream is stopped on unmount so the camera light doesn't linger after the interview.
 */
export type CameraReason = "denied" | "notfound" | "unsupported" | "insecure" | "error" | null;
export function useCameraPreview(): {
  videoRef: RefObject<HTMLVideoElement | null>;
  active: boolean;
  denied: boolean;
  reason: CameraReason;
  stream: MediaStream | null;
  /** Re-attempt camera acquisition — the strict-mode pause overlay's "try again". Additive: a
   *  caller that never retries behaves exactly as before (one acquisition on mount). */
  retry: () => void;
} {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [active, setActive] = useState(false);
  const [denied, setDenied] = useState(false);
  const [reason, setReason] = useState<CameraReason>(null);
  // Bumped by retry(); each bump re-runs the acquisition effect (the cleanup stops the old
  // stream first, so a retry can never leak a second camera claim / camera light).
  const [attempt, setAttempt] = useState(0);
  // Exposed so the recorder can reuse this exact camera track (adding its own mic) instead of
  // opening a second camera. It's reactive state, not just the ref, so a consumer's effect can
  // wait for the stream to actually resolve before it starts recording.
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    let cancelled = false;
    // A retry starts from a clean slate — a stale "denied" from the last attempt must not sit on
    // screen while a now-granted camera is negotiating. No-ops on the first run.
    setActive(false);
    setDenied(false);
    setReason(null);
    setStream(null);
    const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
    if (!md?.getUserMedia) {
      // getUserMedia is absent on http:// (non-localhost) and old browsers — tell them apart so the
      // message is honest: an insecure page vs a browser that just can't do it.
      const insecure = typeof window !== "undefined" && !window.isSecureContext;
      setReason(insecure ? "insecure" : "unsupported");
      setDenied(true);
      return;
    }
    md.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        setStream(s);
        setActive(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err instanceof DOMException ? err.name : "";
        setReason(
          name === "NotAllowedError" || name === "SecurityError"
            ? "denied"
            : name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError"
              ? "notfound"
              : "error",
        );
        setDenied(true);
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [attempt]);

  const retry = useCallback((): void => setAttempt((a) => a + 1), []);

  return { videoRef, active, denied, reason, stream, retry };
}

/** Human-friendly, honest one-liner for why the camera isn't on — reused across check-in variants. */
export function cameraReasonText(reason: CameraReason): string {
  switch (reason) {
    case "denied":
      return "Camera access is blocked. You can still continue — or enable it in your browser's site settings and reload.";
    case "notfound":
      return "No camera was found. That's fine — you can still continue; interviews are assessed on your answers.";
    case "insecure":
      return "Your camera needs a secure (https) connection. You can still continue without it.";
    case "unsupported":
      return "This browser can't use the camera. You can still continue — interviews are assessed on your answers.";
    default:
      return "Your camera is off — that's fine, you can still continue. Interviews are assessed on your answers, not your video.";
  }
}

/**
 * Whether to put the candidate in front of the CODE EDITOR rather than the voice/typing surface.
 *
 * The kind alone is not enough. A `code` row's exercise — its language, starter and hidden tests —
 * comes from the competency's exercise on the vertical (migration 0039); without it there is
 * nothing to render an editor around, and the old `kind === "code"` checks would then hide the
 * voice pane AND the typing pane and leave the candidate staring at a question they have no way to
 * answer, mid-interview, with no way out. Spec present → editor. Spec missing → answer it like any
 * other question, which the API accepts.
 */
function hasCodeEditor(q: StateQuestion | null): boolean {
  return !!q && q.kind === "code" && !!q.codeSpec;
}

/**
 * The statements to render as pick-buttons for a snippet-choice question (#78), or null.
 *
 * Same shape of defence as hasCodeEditor: the kind alone is not enough. Options belong to a
 * code_reading question, and fewer than two of them is not a choice — a lone stray option (a
 * hand-edited row, a half-migrated one) degrades to the plain read-and-explain flow this kind has
 * always had, never to a button the candidate is forced to press.
 */
function choiceOptions(q: StateQuestion | null): string[] | null {
  if (!q || q.kind !== "code_reading") return null;
  return q.options && q.options.length >= 2 ? q.options : null;
}

// ---- STRICT mode (interview preset #43) ------------------------------------------------------
// Strict is PAUSE-NEVER-TERMINATE by construction: every gate below can suspend the interview,
// none can end it, fail it, or block scoring — signals are read by a human, decisions are made by
// a human. And every gate has an escape hatch (the PR#17 lesson: removing the typed fallback
// trapped candidates with broken mics; a broken webcam or flaky fullscreen must never dead-end an
// honest candidate the same way).

/** Why the interview is paused right now — also the strict_pause detail vocabulary. */
type StrictPauseReason = "no_face" | "fullscreen_exit" | "camera_lost";
/** What the candidate can bypass. no_face and camera_lost are both failures of the CAMERA
 *  requirement — bypassing either waives the camera, not just one symptom of losing it. */
type StrictRequirement = "camera" | "fullscreen";

const REQUIREMENT_OF: Record<StrictPauseReason, StrictRequirement> = {
  no_face: "camera",
  fullscreen_exit: "fullscreen",
  camera_lost: "camera",
};

/** Pause-overlay copy. Named, specific, blame-free: what happened, what fixes it, and that the
 *  interview is waiting — never lost, never failed. No "violation", no "warning". */
const PAUSE_COPY: Record<StrictPauseReason, { title: string; body: string; fix: string | null }> = {
  fullscreen_exit: {
    title: "You've left fullscreen",
    body: "This session runs in fullscreen. Re-enter it and we'll pick up exactly where you left off — your question and your answer so far are saved.",
    fix: "Re-enter fullscreen",
  },
  camera_lost: {
    title: "Your camera has disconnected",
    body: "Check it's plugged in and still allowed in your browser's site settings, then try again. Nothing is lost while you sort it out.",
    fix: "Try my camera again",
  },
  no_face: {
    title: "We can't see you on camera",
    body: "Step back into frame when you're ready — the interview continues automatically the moment we can see you. Take the time you need.",
    fix: null, // being visible IS the fix; the presence machine confirms it and resumes on its own
  },
};

/** After this long stuck in one pause, the escape hatch appears — a candidate who cannot satisfy
 *  a requirement is offered a way through, not a wall. */
const STRICT_HATCH_AFTER_S = 60;
/** …or after this many pauses for the same reason: a webcam that keeps dropping is a technical
 *  limitation to note, not a loop to trap someone in. */
const STRICT_HATCH_AFTER_PAUSES = 2;

/**
 * OFF by founders' decision (18 Aug 2026, Hafiz + Cader): a live face-derived "Focus %" in
 * front of the candidate contradicts the product's stated posture ("facial AI is proof only —
 * it never scores face") and re-creates the mid-answer anxiety the transcript-hiding removed.
 * The face-mesh overlay itself stays — it signals transparently that proctoring is active.
 * The metrics still compute client-side; flip to true only with a deliberate product decision.
 */
const SHOW_FOCUS_METRICS = false;

export function InterviewStep({
  token,
  orgName,
  requisitionTitle,
  onAudioCue,
  interviewMode,
  language: inviteLanguage,
}: {
  token: string;
  orgName?: string;
  requisitionTitle?: string;
  /** WS4: the language chosen at consent, from the invite payload — the first-render value, so
   *  the speech engines are created on the right locale before the state payload lands. The
   *  state payload's session.language overrides it once it arrives (the session row is what the
   *  API actually minted the speech token from). Absent or unknown reads as English. */
  language?: string;
  /** Publishes the turn-taking cue (idle/ai/candidate + a 0–1 level) for the shell's ambient
   *  background wave — see CandidateBackground.tsx. Optional so this component still works
   *  standalone (tests, storybook-style usage) without a shell wired up. */
  onAudioCue?: (cue: AudioCue) => void;
  /** The requisition's proctoring preset, from the invite payload. Absent (an old cached payload,
   *  an older API) means "natural" — the same interviewModeOf default as everywhere else, so a
   *  stale client can only ever UNDER-enforce, never surprise a candidate with strictness they
   *  were not told about at consent. */
  interviewMode?: InterviewMode;
}): ReactNode {
  const queryClient = useQueryClient();
  // The interview state is fetched FIRST because the speech engines below need the session
  // language before they are created; everything derived from `data` (progress, question) is
  // read further down where it always was.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["interviewState", token],
    queryFn: () => api.get<InterviewStateResponse>(`/invite/${token}/interview/state`),
  });
  // WS4: the ONE language value the screen runs on. Session row first (what the API minted the
  // speech token and rendered the prompts from), invite prop until it lands, English for anything
  // absent or unknown — an older API, the pre-migration window, a code this build never heard of.
  const language = interviewLanguageOf(data?.session?.language ?? data?.language ?? inviteLanguage);
  // ---- speech engines (Track 2, docs/VOICE_STACK_DECISION.md) ----
  // Azure AI Speech (Central India) when the server grants a session; the Web Speech engines
  // stay as the fallback so a token failure can never trap a candidate. Both hook pairs always
  // run (rules of hooks); only the SELECTED pair is ever driven — the other stays idle.
  const { session: speechSession, tokenRef: speechTokenRef } = useSpeechSession(token);
  const azureOn = speechSession?.enabled === true;
  // Live Azure Speech usage → ai_call_log (measurement only; null while Web Speech is in use).
  const speechUsage = useSpeechUsageReporter(token, BASE_URL, azureOn);
  const webSpeech = useAshaSpeech(language);
  const azureAsha = useAzureAshaSpeech(azureOn, speechSession, speechTokenRef, speechUsage);
  const speech = azureOn ? azureAsha : webSpeech;
  const strict = (interviewMode ?? "natural") === "strict";
  const [agreedFullscreen, setAgreedFullscreen] = useState(false);

  // ---- the mic gate ----------------------------------------------------------------------
  // `guardUntil` is the moment the mic may reopen after Asha stops. It lives in BOTH a ref and
  // state on purpose: the ref is read synchronously from SpeechRecognition's own callback, the
  // state drives the render that recomputes whether we should be listening.
  const guardUntilRef = useRef(0);
  const [guardUntil, setGuardUntil] = useState(0);
  const acceptResult = useCallback(
    (at: number) =>
      shouldAcceptResult({ speaking: speech.speakingRef.current, guardUntil: guardUntilRef.current, at }),
    [speech.speakingRef],
  );
  const webVoice = useVoiceInput(acceptResult, language);
  const azureVoice = useAzureVoiceInput(azureOn, speechSession, speechTokenRef, acceptResult, speechUsage);
  const voice = azureOn ? azureVoice : webVoice;
  const [tabAlert, setTabAlert] = useState(false);
  /** Notice keys the candidate has dismissed from the compact notice area (presentational only). */
  const [dismissedNotes, setDismissedNotes] = useState<ReadonlySet<string>>(() => new Set());
  const pageHidden = useDocumentHidden();

  useEffect(() => {
    if (pageHidden) {
      setTabAlert(true);
    }
  }, [pageHidden]);

  const cam = useCameraPreview();
  const { level: micAmplitude, start: startMicMeter, stop: stopMicMeter } = useMicLevel();
  // REAL background blur: segments the person and blurs only what's behind them. Off by default,
  // and it never touches cam.stream — the recorder and the proctor's face sampler both keep the
  // raw, unblurred track. See useBackgroundBlur for the honest-degradation rules.
  const blur = useBackgroundBlur(cam.videoRef, cam.stream);

  // ---- strict enforcement state (all of it inert when the mode is natural) ----
  // useCameraPreview never notices its own track dying (nothing re-renders on 'ended'), which was
  // fine while the camera was decoration. Strict watches the track so an unplugged webcam pauses
  // the interview instead of silently recording a black rectangle for twenty minutes.
  const [camLost, setCamLost] = useState(false);
  const cameraLive = cam.active && !camLost;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { focusMetrics, focusMessage } = useFaceMesh(cam.videoRef, canvasRef, cameraLive ? 'recording' : 'idle');
  const [isFullscreen, setIsFullscreen] = useState(
    () => typeof document !== "undefined" && document.fullscreenElement != null,
  );
  // iPhone Safari has no element fullscreen at all; a strict gate that demands it there would
  // dead-end every iPhone candidate — the exact trapped-candidate bug this feature must not have.
  const fullscreenSupported =
    typeof document !== "undefined" &&
    typeof document.documentElement.requestFullscreen === "function" &&
    document.fullscreenEnabled !== false;
  const [fsFailed, setFsFailed] = useState(false); // requestFullscreen threw/rejected — offer the hatch
  const [strictPause, setStrictPause] = useState<{ reason: StrictPauseReason; since: number } | null>(null);
  const strictPauseRef = useRef(strictPause);
  strictPauseRef.current = strictPause;
  // Requirements the candidate (or an unsupporting browser) has bypassed — degraded-strict: that
  // one requirement stops being enforced, everything else stays on.
  const [bypassed, setBypassed] = useState<ReadonlySet<StrictRequirement>>(() => new Set());
  const pauseCountsRef = useRef<Record<StrictPauseReason, number>>({
    no_face: 0,
    fullscreen_exit: 0,
    camera_lost: 0,
  });
  const [pauseElapsed, setPauseElapsed] = useState(0);
  // strict_bypass decisions made BEFORE proctoring starts (pre-flight) — enqueue drops events
  // while inactive, so these wait here and flush right after proctor.start().
  const preStartBypassesRef = useRef<string[]>([]);
  // True exactly while the candidate holds the speaking turn (the applied mic intent). The gaze
  // machine samples only inside this window — thinking time is free by design.
  const answeringRef = useRef(false);
  const micOpenRef = useRef(false);
  const lastSpeechAtRef = useRef(0);
  const lastTranscriptRef = useRef("");

  // Additive integrity + recording. Both are best-effort and degrade silently — they never gate,
  // block or regress the question/answer/voice flow below. Wired to the interview lifecycle in an
  // effect further down: start once questions are live, finalize once the interview wraps up.
  // `cameraLive` is load-bearing, not decoration: it is the ONLY thing that lets the proctor
  // sample the camera. Pass a live camera and presence works; pass a denied one and the detector
  // is never downloaded, never read and never emits — which is the honest answer, not a flag.
  const proctor = useProctoring(token, cam.videoRef, cameraLive, {
    mode: strict ? "strict" : "natural",
    answeringRef,
  });
  const recorder = useRecorder(token, cam.stream);
  const proctorStartedRef = useRef(false);
  const recorderStartedRef = useRef(false);
  const captureFinalizedRef = useRef(false);

  const [mode, setMode] = useState<AnswerMode>("voice");
  const [showText, setShowText] = useState(true); // question text is shown by default (candidate asked)
  const [revealedOnce, setRevealedOnce] = useState(false);
  const [typedText, setTypedText] = useState("");
  /**
   * DEFECT-CLASS FIX ("here's what I heard", 6 Aug 2026): the spoken transcript is EDITABLE
   * before it is sent. null = untouched (the live transcript is the answer, answeredBy "voice");
   * a string = the candidate corrected what the engine misheard (answeredBy "voice_edited" — the
   * report can say so, and the recording-verification pass can check the edit against the audio).
   * New final results arriving AFTER an edit are APPENDED to it, so correcting one word early in
   * a long answer doesn't freeze the rest of the capture out.
   */
  const [voiceEdit, setVoiceEdit] = useState<string | null>(null);
  const prevFinalRef = useRef("");
  const [code, setCode] = useState("");
  /** Snippet choice (#78): the option the candidate picked, locked once made. Null = not picked
   *  yet (or the question carries no options at all). */
  const [picked, setPicked] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const int = setInterval(() => {
      // A strict pause freezes the visible clock — a paused candidate staring at a timer that
      // keeps counting against them would read the pause as a penalty, which it is not.
      if (strictPauseRef.current === null) setElapsed(e => e + 1);
    }, 1000);
    return () => clearInterval(int);
  }, []);

  const mins = Math.floor(elapsed / 60).toString().padStart(2, "0");
  const secs = (elapsed % 60).toString().padStart(2, "0");
  const [caption, setCaption] = useState("");
  const startedAtRef = useRef(Date.now());

  const progress: ProgressPayload = data?.progress ?? { answered: 0, total: 0 };
  const question = data?.question ?? null;
  const notStarted = !isLoading && !isError && progress.total === 0;
  const questionId = question?.id ?? null;
  // The counter, the bar and the "is this the last one" claim, all off the PLAN rather than off a
  // live row count the adaptive engine grows mid-interview. See interviewProgress.ts.
  const view = progressView(progress, question?.kind ?? null);

  // ---- resumed session ----------------------------------------------------------------------
  // Closing the tab and coming back resumes at the right question and always did — silently, which
  // reads like the earlier answers were lost. Captured from the FIRST state payload only: after
  // that, `answered` grows because of THIS sitting, and a "welcome back" per question would be
  // nonsense. Self-clearing and dismissible; it never gates anything.
  const [resumeNote, setResumeNote] = useState<string | null>(null);
  const resumeCheckedRef = useRef(false);
  useEffect(() => {
    if (resumeCheckedRef.current || data === undefined) return;
    resumeCheckedRef.current = true;
    if (data.progress.answered <= 0 || data.question === null) return;
    const at = progressView(data.progress, data.question.kind);
    setResumeNote(
      at.onFollowUp
        ? "Welcome back — your earlier answers are saved. Picking up where you left off."
        : `Welcome back — your earlier answers are saved. Picking up at question ${at.position} of ${at.planned}.`,
    );
  }, [data]);
  useEffect(() => {
    if (resumeNote === null) return undefined;
    const t = window.setTimeout(() => setResumeNote(null), 15_000);
    return () => window.clearTimeout(t);
  }, [resumeNote]);

  // New question → reset per-question answer state, restore any typed draft, and have Asha
  // speak it. Text stays hidden unless we must fall back (no TTS, or a code question).
  //
  // Held until the speech session has SETTLED (Azure granted or declined): speaking the first
  // question in the fallback voice and then switching to Asha's real voice mid-interview would
  // be stranger than the ~300ms the decision takes. When it settles, the dep flip re-runs this
  // effect and the question is spoken by the engine that will finish the interview.
  const speechSettled = speechSession !== undefined;
  const prevQuestionRef = useRef<string | null>(null);
  useEffect(() => {
    if (question === null) {
      setTypedText("");
      setCode("");
      setPicked(null);
      setCaption("");
      return;
    }
    if (!speechSettled) return;
    voice.reset();
    setVoiceEdit(null);
    prevFinalRef.current = "";
    setTypedText(readDraft(question.id, "answer") ?? "");
    setCode(readDraft(question.id, "code") ?? question.codeSpec?.starter ?? "");
    // A pick made before a refresh comes back locked — it was locked the moment it was made.
    // A draft OUTSIDE the question's option range (only reachable by hand-editing storage) is
    // discarded rather than restored: a locked invalid pick would 400 on submit with no UI way out.
    const pickDraft = readDraft(question.id, "pick");
    const optionCount = question.options?.length ?? 0;
    const pickN = pickDraft !== null && /^\d+$/.test(pickDraft) ? Number(pickDraft) : null;
    setPicked(pickN !== null && pickN >= 0 && pickN < optionCount ? pickN : null);
    startedAtRef.current = Date.now();

    const isCode = hasCodeEditor(question);
    // Always show the question text as Asha speaks it (per candidate feedback), on every question.
    setShowText(true);
    setRevealedOnce(true);
    setRephraseNote(null); // a fresh question deserves a clean slate, not a stale "can't simplify"
    // Spoken answers only (Oct 2026, owner decision): no typed fallback. A code question has its
    // own editor; a dead mic gets the fix-mic panel below, never a keyboard.
    void isCode;
    setMode("voice");

    const line = spokenFor(question);
    setCaption(line);
    // A question can arrive while a strict pause overlay is up (the previous answer's submit was
    // already in flight when the pause fired). Speaking it behind the overlay would contradict
    // "paused", so the resume effect re-speaks the caption instead — and the pause clock restarts
    // here, because the resume adjustment must only credit pause time this question actually saw.
    if (strictPauseRef.current !== null) {
      setStrictPause((prev) => (prev === null ? prev : { ...prev, since: Date.now() }));
    } else {
      // A brief acknowledgment before every question after the first — candidates read the
      // silence after an answer as "did it even hear me?". Canned and rotated, never generated:
      // an acknowledgment must never characterise or judge the answer it follows. In the session
      // language (SPOKEN_ACKS_BY_LANGUAGE), or a Tamil question would arrive framed in English.
      const isFollowOn = prevQuestionRef.current !== null && prevQuestionRef.current !== question.id;
      speech.speak(isFollowOn ? `${ackFor(language, question.seq)} ${line}` : line);
    }
    prevQuestionRef.current = question.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, speechSettled]);

  // Persist typed/code drafts per question.
  useEffect(() => {
    if (questionId === null) return;
    writeDraft(questionId, "answer", typedText);
    writeDraft(questionId, "code", code);
  }, [questionId, typedText, code]);

  // ---- camera-presence notice, made legible -------------------------------------------------
  // `proctor.cameraNotice` is only non-null WHILE the candidate is out of frame — i.e. only while
  // they are not looking at the screen — so the one person it is written for is the one person who
  // never reads it. The view keeps a calm confirmation up for a few seconds after they return.
  // Thresholds are untouched: the presence machine still needs 5s + 4 consecutive readings.
  const [camNotice, setCamNotice] = useState<CameraNoticeState>(IDLE_NOTICE);
  useEffect(() => {
    setCamNotice((prev) => nextCameraNotice(prev, proctor.cameraNotice, Date.now()));
  }, [proctor.cameraNotice]);
  useEffect(() => {
    const at = camNotice.clearAt;
    if (at === null) return undefined;
    // Its own clock: once the candidate is simply sitting there in frame, no further presence
    // reading is coming to retire the line for us.
    const t = window.setTimeout(
      () => setCamNotice((prev) => nextCameraNotice(prev, null, Date.now())),
      Math.max(0, at - Date.now()) + 50,
    );
    return () => window.clearTimeout(t);
  }, [camNotice.clearAt]);

  // ---- integrity + recording lifecycle (additive, best-effort) ----
  // "Live" = questions have loaded and one is showing; "wrapping up" = the server has no more
  // questions (last answer submitted). react-query keeps the previous question during a refetch,
  // so this never flickers to "wrapping up" between questions — only at genuine completion.
  const interviewLive = !isLoading && !isError && progress.total > 0 && question !== null;
  const wrappingUp = !isLoading && !isError && progress.total > 0 && question === null;

  // Begin proctoring the moment the interview is live.
  useEffect(() => {
    if (interviewLive && !proctorStartedRef.current) {
      proctorStartedRef.current = true;
      proctor.start();
      // Strict decisions made at pre-flight (continue-without-camera, an unsupported-fullscreen
      // waiver) are part of the record, but enqueue drops events until start() — flush them now
      // or the report never learns the session ran degraded.
      for (const detail of preStartBypassesRef.current) proctor.emitStrict("strict_bypass", detail);
      preStartBypassesRef.current = [];
    }
  }, [interviewLive, proctor]);

  // Begin recording once live AND the camera stream has resolved (it can arrive a beat after the
  // questions do; we wait for it rather than start-and-skip). No camera → no recording, silently.
  useEffect(() => {
    if (interviewLive && cam.stream && !recorderStartedRef.current) {
      recorderStartedRef.current = true;
      recorder.start();
    }
  }, [interviewLive, cam.stream, recorder]);

  // Finalize both when the interview wraps up — the preferred path, while the camera is still live.
  // If the screen unmounts first (server jumps straight to "done"), each hook self-finalizes on
  // unmount, and finalize is idempotent, so nothing double-runs or is lost.
  useEffect(() => {
    if (wrappingUp && (proctorStartedRef.current || recorderStartedRef.current) && !captureFinalizedRef.current) {
      captureFinalizedRef.current = true;
      proctor.stop();
      void recorder.finalize();
    }
  }, [wrappingUp, proctor, recorder]);

  // ---- strict enforcement lifecycle --------------------------------------------------------
  // Watch fullscreen for the pre-flight check and the pause trigger alike.
  useEffect(() => {
    if (!strict || typeof document === "undefined") return undefined;
    const onFs = (): void => setIsFullscreen(document.fullscreenElement != null);
    onFs();
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [strict]);

  // Watch the camera track itself: revoked permission and an unplugged webcam both fire 'ended'
  // without any React state noticing otherwise.
  useEffect(() => {
    if (!strict || !cam.stream) return undefined;
    setCamLost(false); // a fresh stream is a live stream — clears the flag after a successful retry
    const onEnded = (): void => setCamLost(true);
    const tracks = cam.stream.getVideoTracks();
    for (const t of tracks) t.addEventListener("ended", onEnded);
    return () => {
      for (const t of tracks) t.removeEventListener("ended", onEnded);
    };
  }, [strict, cam.stream]);

  // A browser with no fullscreen API (every iPhone) waives that requirement up front — NOTED as a
  // bypass row rather than silently, so the report says the session ran degraded and why.
  const fsWaivedRef = useRef(false);
  useEffect(() => {
    if (!strict || fullscreenSupported || fsWaivedRef.current) return;
    fsWaivedRef.current = true;
    preStartBypassesRef.current.push("fullscreen_unsupported");
    setBypassed((prev) => new Set(prev).add("fullscreen"));
  }, [strict, fullscreenSupported]);

  const enterFullscreen = useCallback((): void => {
    if (typeof document === "undefined") return;
    try {
      // The promise rejects (and older engines throw synchronously) when the browser refuses —
      // permission policy, an iframe, a gesture that expired. Either way the candidate gets the
      // hatch instead of a button that silently does nothing.
      document.documentElement.requestFullscreen()?.catch(() => setFsFailed(true));
    } catch {
      setFsFailed(true);
    }
  }, []);

  const requireFullscreen = strict && fullscreenSupported && !bypassed.has("fullscreen");
  const requireCamera = strict && !bypassed.has("camera");

  // PAUSE TRIGGER — one effect, one pause at a time, first cause wins. Never on a single missed
  // frame: the no_face arm rides the presence machine's own generous confirmation (5s + 4
  // consecutive readings, surfaced through cameraNotice), and the other two arms are hard
  // browser facts, not detector guesses.
  useEffect(() => {
    if (!strict || !interviewLive || strictPause !== null) return;
    const reason: StrictPauseReason | null =
      requireFullscreen && !isFullscreen
        ? "fullscreen_exit"
        : requireCamera && !cameraLive
          ? "camera_lost"
          : requireCamera && proctor.cameraNotice?.status === "absent"
            ? "no_face"
            : null;
    if (reason === null) return;
    pauseCountsRef.current[reason] += 1;
    proctor.emitStrict("strict_pause", reason);
    // Quiet as well as visible: Asha stops mid-sentence, and the mic gate stays shut on its own
    // (strictPause forces the applied intent off in the gate effect below).
    speech.cancel();
    setStrictPause({ reason, since: Date.now() });
  }, [strict, interviewLive, strictPause, requireFullscreen, isFullscreen, requireCamera, cameraLive, proctor, speech]);

  // RESUME — the moment the named cause is fixed (or its requirement bypassed), with a receipt
  // carrying how long the pause lasted. Asha re-anchors the candidate on the current question.
  useEffect(() => {
    if (strictPause === null) return;
    const fixed =
      strictPause.reason === "fullscreen_exit"
        ? isFullscreen || !requireFullscreen
        : strictPause.reason === "camera_lost"
          ? cameraLive || !requireCamera
          : proctor.cameraNotice?.status !== "absent" || !requireCamera;
    if (!fixed) return;
    const pausedMs = Date.now() - strictPause.since;
    proctor.emitStrict("strict_resume", formatStrictResume(strictPause.reason, pausedMs));
    // The per-answer duration clock must not bill the pause to the candidate.
    startedAtRef.current += pausedMs;
    setStrictPause(null);
    if (caption.trim()) speech.speak(caption);
  }, [strictPause, isFullscreen, requireFullscreen, cameraLive, requireCamera, proctor, speech, caption]);

  // The escape-hatch clock for the current pause.
  useEffect(() => {
    if (strictPause === null) {
      setPauseElapsed(0);
      return undefined;
    }
    const since = strictPause.since;
    const int = window.setInterval(() => setPauseElapsed(Math.round((Date.now() - since) / 1000)), 1_000);
    return () => window.clearInterval(int);
  }, [strictPause]);

  const hatchAvailable =
    strictPause !== null &&
    (pauseElapsed >= STRICT_HATCH_AFTER_S || pauseCountsRef.current[strictPause.reason] >= STRICT_HATCH_AFTER_PAUSES);

  // THE ESCAPE HATCH (invariant: no trapped candidate). Waives the ONE unmet requirement, notes
  // it honestly, and moves on — degraded-strict, not failed-strict. No strict_resume: the
  // timeline reads pause → bypass, which is exactly what happened.
  const bypassStrict = useCallback((): void => {
    const pause = strictPauseRef.current;
    if (pause === null) return;
    proctor.emitStrict("strict_bypass", pause.reason);
    setBypassed((prev) => new Set(prev).add(REQUIREMENT_OF[pause.reason]));
    startedAtRef.current += Date.now() - pause.since;
    setStrictPause(null);
    if (caption.trim()) speech.speak(caption);
  }, [proctor, caption, speech]);

  // Good manners: strict claimed the whole screen for the interview — hand it back when the
  // interview is over rather than leaving the candidate to find the Esc key.
  useEffect(() => {
    if (!strict || !wrappingUp || typeof document === "undefined") return;
    if (document.fullscreenElement != null) void document.exitFullscreen().catch(() => { });
  }, [strict, wrappingUp]);

  // Strict paste policy: the paste never lands, its length is logged (paste_blocked, zero
  // penalty). Natural keeps the advisory flag it always had. Attached to BOTH answer textareas.
  const answerPaste = useCallback(
    (e: { clipboardData: DataTransfer | null; preventDefault?: () => void }): void => {
      if (strict) {
        e.preventDefault?.();
        proctor.onPasteBlocked(e.clipboardData?.getData("text")?.length ?? 0);
        return;
      }
      proctor.onPaste(e);
    },
    [strict, proctor],
  );

  const start = useMutation({
    mutationFn: () => api.post<unknown>(`/invite/${token}/interview/start`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["interviewState", token] });
    },
  });

  const [rephraseNote, setRephraseNote] = useState<string | null>(null);
  const rephrase = useMutation({
    mutationFn: (q: StateQuestion) =>
      api.post<{ prompt: string; unchanged?: boolean }>(`/invite/${token}/interview/rephrase`, { questionId: q.id }),
    onSuccess: (res) => {
      // Honesty over silence (QA #66): when the wording can't get simpler, SAY so instead of
      // repeating the same sentence as if it were new.
      if (res?.unchanged) {
        setRephraseNote("That's already as simply as it can be put — tap Repeat to hear it again.");
        return;
      }
      setRephraseNote(null);
      const text = res?.prompt?.trim();
      if (text) {
        setCaption(text);
        speech.speak(text);
      }
    },
  });

  const answer = useMutation({
    mutationFn: (p: {
      q: StateQuestion;
      answerText?: string;
      code?: { language: string; source: string };
      pickedIndex?: number;
      answeredBy: "typed" | "voice" | "voice_edited";
      textRevealed: boolean;
    }) =>
      api.post<AnswerResponse>(`/invite/${token}/interview/answer`, {
        questionId: p.q.id,
        answerText: p.answerText,
        code: p.code,
        // Snippet choice (#78): the pick travels with the spoken/typed explanation; the server
        // checks it against the stored answer key — correctness is never decided here.
        pickedIndex: p.pickedIndex,
        durationSeconds: Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000)),
        answeredBy: p.answeredBy,
        textRevealed: p.textRevealed,
      }),
    onSuccess: (res, p) => {
      clearDraft(p.q.id);
      // Seed from the answer's OWN response instead of waiting on the refetch. Between the
      // mutation resolving and the refetch landing, the screen kept showing the question just
      // answered — same number, same bar, submit button live again — which is most of what
      // "the progress indicator is not consistently updated" looked like from the outside.
      if (res?.progress) {
        queryClient.setQueryData<InterviewStateResponse>(["interviewState", token], (prev) =>
          prev
            ? { ...prev, progress: res.progress!, question: res.done ? null : (res.question ?? null) }
            : prev,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["interviewState", token] });
      void queryClient.invalidateQueries({ queryKey: ["invite", token] });
    },
  });

  // 2-way voice control: a short spoken "repeat that" re-speaks the question; "can you clarify"
  // rephrases it — so the candidate never has to reach for a button mid-answer. Guarded to short
  // command-like phrases so a real answer that happens to contain a keyword isn't swallowed.
  useEffect(() => {
    const t = voice.finalText.trim().toLowerCase();
    if (!t || mode !== "voice" || question === null || speech.speaking) return;
    const words = t.split(/\s+/).filter(Boolean).length;
    const repeatRe =
      /\b(repeat|say (that|it) again|again please|didn'?t (get|catch|hear)|come again|pardon|once more|what was the question)\b/;
    const clarifyRe =
      /\b(clarify|what do you mean|rephrase|explain the question|didn'?t understand the question|can you explain the question)\b/;
    if (words <= 9 && repeatRe.test(t)) {
      voice.reset();
      speech.speak(caption);
    } else if (words <= 12 && clarifyRe.test(t) && !rephrase.isPending) {
      voice.reset();
      rephrase.mutate(question);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.finalText]);

  // Append newly-recognised words to an edited transcript. Without this, one early correction
  // would silently discard everything the candidate said afterwards.
  useEffect(() => {
    const delta = voice.finalText.slice(prevFinalRef.current.length).trim();
    prevFinalRef.current = voice.finalText;
    if (delta && voiceEdit !== null) setVoiceEdit((v) => `${v ?? ""} ${delta}`.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.finalText]);

  // ---- THE MIC GATE --------------------------------------------------------------------------
  // Sequence for one turn: speak → guard → listen → result.
  //
  //  speak()  raises `speaking` SYNCHRONOUSLY and slams the guard shut, so the mic is already off
  //           before the first syllable and any result still in flight is discarded.
  //  guard    once she stops, the mic stays shut for TTS_TAIL_GUARD_MS: there is no echo
  //           cancellation for synthesised speech, so the tail of the utterance comes back
  //           through the speakers a beat later and would otherwise be transcribed as the answer.
  //  listen   the guard expires → intent flips true → the recogniser starts (and is restarted by
  //           the hook whenever Chrome ends it on its own).
  //  result   accepted only if it arrived after the guard, checked again inside onresult.
  const wasSpeakingRef = useRef(false);
  const [, bumpGuardTick] = useState(0);
  useEffect(() => {
    if (speech.speaking) {
      wasSpeakingRef.current = true;
      // Closed outright while she talks — a stray result cannot slip through on a stale flag.
      guardUntilRef.current = Number.MAX_SAFE_INTEGER;
      setGuardUntil(Number.MAX_SAFE_INTEGER);
      return undefined;
    }
    if (!wasSpeakingRef.current) return undefined; // she has not spoken yet; nothing to guard
    wasSpeakingRef.current = false;
    // Azure TTS renders through the page (SpeakerAudioDestination), so the browser's echo
    // canceller can see and subtract it — the 450ms deaf window shrinks to a token 150ms that
    // only covers AEC convergence. Web Speech keeps the full guard: its audio is OS-level and
    // invisible to AEC, which is the whole reason the guard existed.
    const guardMs = azureOn ? AZURE_TAIL_GUARD_MS : TTS_TAIL_GUARD_MS;
    const until = guardAfterSpeech(Date.now(), guardMs);
    guardUntilRef.current = until;
    setGuardUntil(until);
    // Nothing re-renders merely because a timestamp passed, so wake the decision below up exactly
    // when the guard expires — otherwise the mic would wait for some unrelated render.
    const t = window.setTimeout(() => bumpGuardTick((n) => n + 1), guardMs + 20);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.speaking, azureOn]);

  // ONE place decides whether the mic should be live, from inputs only. It never reads
  // `voice.listening` — reading the state it also mutates is what made the old effect retrigger
  // itself on every render and flicker the mic (and the transcript) many times a second.
  const listenIntent = shouldListen({
    supported: voice.supported,
    micBlocked: voice.blocked,
    pageHidden,
    mode,
    hasQuestion: question !== null,
    usesCodeEditor: hasCodeEditor(question),
    speaking: speech.speaking,
    pending: answer.isPending,
    guardUntil,
    now: Date.now(),
  });
  useEffect(() => {
    // `speakingRef` is consulted again here because speak() runs from an event handler or from the
    // question-change effect — i.e. AFTER this render's `listenIntent` was computed but BEFORE
    // React re-renders with speaking=true. Without it the mic opens for one commit at the exact
    // moment Asha starts a question. The flag going down always causes a re-render, so this can
    // only ever delay listening, never prevent it.
    //
    // A strict pause is a hard mute on top of the gate (the overlay is up; anything said now
    // belongs to nobody), applied here rather than inside shouldListen so the tested gate stays
    // untouched. `micOpenRef` mirrors the APPLIED intent; the gaze gate (`answeringRef`) is
    // stricter than the mic — see the speech-activity effect below.
    const want = listenIntent && !speech.speakingRef.current && strictPause === null;
    micOpenRef.current = want;
    if (!want) answeringRef.current = false; // mic closing closes the gaze window immediately
    voice.setIntent(want);
  }, [listenIntent, voice, speech.speakingRef, strictPause]);

  // Gaze is measured only while the candidate is actually SPEAKING — not merely while the mic is
  // open. An open mic covers silent thinking time too, and a candidate who turns to stare out of
  // the window while composing an answer has done nothing reportable; the consent screen and the
  // report both promise exactly that. "Speaking" = the recogniser produced text within the last
  // few seconds, so mid-sentence breaths don't blink the window while genuine silent pauses close
  // it. The pattern strict mode exists to observe — reading from a second screen WHILE talking —
  // streams interim text continuously and stays measured throughout.
  useEffect(() => {
    const text = voice.transcript.trim();
    if (text.length > 0 && text !== lastTranscriptRef.current) {
      lastTranscriptRef.current = text;
      lastSpeechAtRef.current = Date.now();
    }
  }, [voice.transcript]);
  useEffect(() => {
    if (!strict || !interviewLive) {
      answeringRef.current = false;
      return undefined;
    }
    const tick = (): void => {
      answeringRef.current = micOpenRef.current && Date.now() - lastSpeechAtRef.current < SPEAKING_HOLD_MS;
    };
    tick();
    const timer = setInterval(tick, 1_000);
    return () => {
      clearInterval(timer);
      answeringRef.current = false;
    };
  }, [strict, interviewLive]);

  // The mic is gone for good → hand the candidate a keyboard rather than let them talk into a
  // dead microphone. They are never trapped, and we say plainly what happened (banner below).
  // Whatever they already said comes with them: losing a half-finished answer to a hardware
  // problem would be the candidate paying for our fault.
  // (Mic gone for good → the fix-mic panel takes over the answer area; whatever was already said
  // stays in voice.transcript and can still be submitted.)

  // ---- ambient background turn-taking cue (visual only — see CandidateBackground.tsx) ----
  // "ai": a smooth synthesized pulse (no accessible amplitude for speechSynthesis output).
  // "candidate": REAL mic amplitude from a dedicated getUserMedia stream (separate from
  // SpeechRecognition's own internal mic, which exposes no level). Neither is shown as data
  // anywhere in the UI — purely a background cue.
  //
  // The meter follows the TURN, not the recogniser. Chrome ends recognition every few seconds and
  // we restart it; tearing the getUserMedia stream down and back up with each of those blinked
  // the OS mic indicator and cost a beat of dead air on every restart.
  const micMeterWanted = !!onAudioCue && interviewLive && mode === "voice" && !hasCodeEditor(question) && !voice.blocked;
  useEffect(() => {
    if (micMeterWanted) startMicMeter();
    else stopMicMeter();
  }, [micMeterWanted, startMicMeter, stopMicMeter]);

  useEffect(() => {
    if (!onAudioCue) return undefined;
    if (speech.speaking) {
      // A timer, not requestAnimationFrame: this publishes into the shell's state, so 60fps here
      // meant re-rendering the whole interview 60 times a second. 20fps is plenty for a wave.
      const t0 = performance.now();
      const int = window.setInterval(() => {
        const t = (performance.now() - t0) / 1000;
        onAudioCue({ mode: "ai", level: 0.35 + 0.25 * Math.sin(t * 3.1) });
      }, 50);
      onAudioCue({ mode: "ai", level: 0.35 });
      return () => window.clearInterval(int);
    }
    onAudioCue(listenIntent ? { mode: "candidate", level: 0 } : { mode: "idle", level: 0 });
    return undefined;
  }, [speech.speaking, listenIntent, onAudioCue]);

  useEffect(() => {
    if (onAudioCue && listenIntent && !speech.speaking) onAudioCue({ mode: "candidate", level: micAmplitude });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micAmplitude]);

  const submit = useCallback(() => {
    if (question === null) return;
    speech.cancel();
    // Drop the mic immediately rather than waiting for the next render to work it out — anything
    // said from here on belongs to no question.
    voice.setIntent(false);
    const pickedIndex = choiceOptions(question) !== null && picked !== null ? picked : undefined;
    if (question.kind === "code" && question.codeSpec) {
      answer.mutate({
        q: question,
        code: { language: question.codeSpec.language, source: code },
        answeredBy: "typed",
        textRevealed: revealedOnce,
      });
    } else if (mode === "voice") {
      // The edited text IS the answer when the candidate touched it — "what you send is what's
      // assessed" is only true if this line honours the edit. Provenance distinguishes a
      // hand-corrected transcript from a raw one; the recording-verification pass on the worker
      // still checks either against the audio.
      const edited = voiceEdit !== null && voiceEdit.trim() !== voice.finalText.trim();
      const answerOut = (voiceEdit ?? voice.transcript).trim();
      answer.mutate({ q: question, answerText: answerOut, pickedIndex, answeredBy: edited ? "voice_edited" : "voice", textRevealed: revealedOnce });
    } else {
      answer.mutate({ q: question, answerText: typedText.trim(), pickedIndex, answeredBy: "typed", textRevealed: revealedOnce });
    }
  }, [question, mode, code, typedText, picked, revealedOnce, answer, speech, voice, voiceEdit]);

  const busy = start.isPending || answer.isPending || rephrase.isPending;
  // Show "Listening" on INTENT, not on the recogniser's own start/stop: Chrome ends recognition
  // every few seconds and we restart it, and blinking the orb through "Ready" each time told the
  // candidate their mic had switched off when their turn had not moved at all.
  const micOpen = listenIntent || voice.listening;
  const orbState = speech.speaking ? "speaking" : micOpen ? "listening" : busy ? "thinking" : "idle";
  const stateLabel =
    orbState === "speaking"
      ? "Speaking"
      : orbState === "listening"
        ? "Listening"
        : orbState === "thinking"
          ? "Thinking"
          : "Ready";

  /** No working microphone for spoken answers — the fix-mic panel replaces the answer area. */
  const micUnavailable = !voice.supported || voice.blocked;

  const canSubmit =
    question !== null &&
    !answer.isPending &&
    // A snippet-choice question needs BOTH the pick and the explanation — an explanation with no
    // pick would reach the judge with half its machine-verified attachment missing.
    (choiceOptions(question) === null || picked !== null) &&
    (hasCodeEditor(question)
      ? code.trim().length > 0
      : mode === "voice"
        ? voice.transcript.trim().length > 0
        : typedText.trim().length > 0);

  // "Finish & submit interview" is a PROMISE, and on the plan path the runtime can still slot a
  // follow-up in after this answer — so it is only made when the server says the total can no
  // longer grow. On the last question we KNOW of, but might not be, the neutral label tells no lie.
  const submitLabel = answer.isPending
    ? "Submitting…"
    : view.submitIntent === "last"
      ? "Finish & submit interview"
      : view.submitIntent === "next"
        ? "Next question"
        // "unknown" = the last question we KNOW of, on a path where a follow-up could still be
        // slotted in. "Submit answer" was so neutral it read like question 3 of 14, so the end of
        // the interview arrived with no warning at all — the honest middle is to say this is the
        // final PLANNED answer while making no promise the interview stops here.
        : "Submit final answer";

  /**
   * Snippet choice (#78): record the pick, lock it, and have Asha ask for the WHY out loud.
   * Locked on first press by design — the pick is a measurement, and letting it be revised after
   * hearing yourself reason about it would measure something else. The spoken why-line raises
   * `speaking`, so the existing mic gate closes and reopens by itself; no new gate logic.
   */
  const pickOption = useCallback(
    (i: number) => {
      if (question === null || picked !== null || answer.isPending) return;
      setPicked(i);
      writeDraft(question.id, "pick", String(i));
      const line = `You picked option ${OPTION_LETTERS[i] ?? String(i + 1)}. Now tell me why — talk me through your reasoning.`;
      setCaption(line);
      speech.speak(line);
    },
    [question, picked, answer.isPending, speech],
  );

  /** Discards the current in-progress answer (voice transcript, typed text, or code-back-to-
   *  starter) without submitting — the candidate keeps the same question and starts over. The
   *  PICK survives a restart on purpose: it locked when it was made (see pickOption), and
   *  "restart my explanation" must not become a quiet way to re-answer the choice. */
  const restartAnswer = useCallback(() => {
    if (question === null) return;
    voice.reset();
    setTypedText("");
    setCode(question.codeSpec?.starter ?? "");
    clearDraft(question.id);
    if (picked !== null) writeDraft(question.id, "pick", String(picked));
  }, [question, picked, voice]);



  return (
    <>
      {/* STRICT PAUSE overlay — suspends, never terminates. It names exactly one cause, offers
          exactly one fix, and after 60s (or a second pause for the same reason) offers the way
          through anyway. The interview state underneath is untouched: question, transcript,
          drafts and timers all wait. */}
      {strict && strictPause !== null && (
        <div
          className="cx-modal-backdrop"
          role="alertdialog"
          aria-modal="true"
          aria-label="Interview paused"
          style={{ position: "fixed", inset: 0, zIndex: 2000, display: "grid", placeItems: "center" }}
        >
          <div className="card pad" style={{ maxWidth: 480, margin: 16, textAlign: "center" }}>
            <h2 style={{ margin: "0 0 6px" }}>{PAUSE_COPY[strictPause.reason].title}</h2>
            <p className="mut" style={{ marginTop: 0 }}>{PAUSE_COPY[strictPause.reason].body}</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
              {strictPause.reason === "fullscreen_exit" && (
                <button className="btn primary" onClick={enterFullscreen}>
                  {PAUSE_COPY.fullscreen_exit.fix}
                </button>
              )}
              {strictPause.reason === "camera_lost" && (
                <button className="btn primary" onClick={cam.retry}>
                  {PAUSE_COPY.camera_lost.fix}
                </button>
              )}
            </div>
            {(hatchAvailable || (strictPause.reason === "fullscreen_exit" && fsFailed)) && (
              <div style={{ marginTop: 14 }}>
                <button className="btn ghost" onClick={bypassStrict}>
                  Continue without {REQUIREMENT_OF[strictPause.reason] === "camera" ? "the camera" : "fullscreen"}
                </button>
                <p className="mut sm" style={{ marginTop: 6 }}>
                  This will be noted for the hiring team as a technical limitation — it is not a mark
                  against you.
                </p>
              </div>
            )}
            <p className="mut sm" style={{ marginBottom: 0 }}>
              Your interview is paused, not ended — your progress and your answer so far are safe.
            </p>
          </div>
        </div>
      )}

      <div className="t1c-iv-cam-col">
        <div className="iv-cam-wrap t1c-iv-cam-wrap">
          {/* Live self-view */}
          <div className={`iv-cam t1c-iv-cam${cameraLive ? "" : " is-off"}`}>
            <video
              ref={cam.videoRef}
              className="iv-video"
              autoPlay
              playsInline
              muted
            />

            <canvas ref={canvasRef} className="t1c-iv-mesh" />

            {/* Camera off / not yet on: say so plainly on the pane instead of a black box with a
                recording badge over it (audit 33 / top-15 #5). */}
            {!cameraLive && (
              <div className="t1c-iv-camoff" role="status">
                <Video />
                <b>{cam.denied || camLost ? "Camera off" : "Starting camera…"}</b>
                {(cam.denied || camLost) && (
                  <>
                    <span>
                      {camLost
                        ? "Your camera stopped — it may be unplugged, or another app took it over."
                        : cameraReasonText(cam.reason)}
                    </span>
                    <button type="button" className="btn sm ghost" onClick={cam.retry}>
                      Try my camera again
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Same decision as the meters (18 Aug): a face-derived judgement is never shown
                to the candidate. This nag survived the flag and was still firing every 4s —
                including whenever the detector simply lost the face, which is exactly the
                lighting- and skin-tone-dependent failure we must not turn into a reprimand. */}
            {SHOW_FOCUS_METRICS && focusMessage && <div className="t1c-iv-focusmsg">{focusMessage}</div>}

            {/* Status chips. "Recording" ONLY when the recorder is actually running on a live
                camera — never a REC badge over a black pane. */}
            <div className="iv-cam-status t1c-iv-status">
              {cameraLive && recorder.recording ? (
                <span className="iv-chip t1c-chip-rec">
                  <span className="dot" /> Recording
                </span>
              ) : cameraLive ? (
                <span className="iv-chip is-live">
                  <span className="dot" /> Camera on · not recording
                </span>
              ) : null}
              <span className="iv-chip">You</span>
            </div>

            {/* Full-width Focus Metrics Strip — off by founders' decision (SHOW_FOCUS_METRICS). */}
            {SHOW_FOCUS_METRICS && (
              <div className="t1c-iv-metrics">
                <div>
                  Focus <b>{focusMetrics.score}%</b>
                </div>
                <div>
                  Eye contact <b>{focusMetrics.eyeContact}%</b>
                </div>
                <div>
                  Head alignment <b>{focusMetrics.headAlignment}%</b>
                </div>
                <div>
                  Stability <b>{focusMetrics.stability}%</b>
                </div>
                <div>
                  Engagement <b>{focusMetrics.facialEngagement}%</b>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="t1c-iv-panel">
        <div className="t1c-iv-panel-inner">
          <div className="t1c-iv-head">
            <div style={{ minWidth: 0 }}>
              <div className="t1c-iv-role" title={requisitionTitle || "Interview"}>
                {requisitionTitle || "Interview"}
              </div>
              <div className="t1c-iv-org">{orgName || "TalentHop"}</div>
            </div>
            <div
              className="t1c-iv-timer"
              title="Elapsed time — there is no time limit"
              aria-label={`Elapsed time ${mins}:${secs} — there is no time limit`}
            >
              <span className="t1c-iv-timer-l">Elapsed</span>
              <span className="tnum">
                {mins}:{secs}
              </span>
            </div>
          </div>

          {/* ONE compact notice area. Every system message the candidate may need (tab switch,
              resumed session, browser limits, lost mic, camera presence) is a short line here,
              each dismissible — instead of a stack of amber boxes above the question. */}
          {(() => {
            type Note = { key: string; tone: "info" | "warn" | "ok"; title: string; body?: string; onDismiss: () => void };
            const dismiss = (k: string) => () => setDismissedNotes((prev) => new Set(prev).add(k));
            const notes: Note[] = [];
            if (tabAlert) {
              notes.push({ key: "tab", tone: "warn", title: "Please stay on this tab during the interview.", onDismiss: () => setTabAlert(false) });
            }
            if (camNotice.showing) {
              notes.push({
                key: `cam-${camNotice.showing.tone}-${camNotice.showing.title}`,
                tone: camNotice.showing.tone === "ok" ? "ok" : "warn",
                title: camNotice.showing.title,
                body: camNotice.showing.body,
                onDismiss: dismiss(`cam-${camNotice.showing.tone}-${camNotice.showing.title}`),
              });
            }
            if (resumeNote) {
              notes.push({ key: "resume", tone: "ok", title: resumeNote, onDismiss: () => setResumeNote(null) });
            }
            if (question !== null && !speech.supported) {
              notes.push({ key: "tts", tone: "info", title: "Audio playback isn't available in this browser, so the question is shown as text below.", onDismiss: dismiss("tts") });
            }
            const shown = notes.filter((n) => n.key === "tab" || n.key === "resume" || !dismissedNotes.has(n.key));
            if (shown.length === 0) return null;
            return (
              <div className="t1c-iv-notes" role="status" aria-live="polite">
                {shown.map((n) => (
                  <div key={n.key} className={`t1c-iv-note ${n.tone}`}>
                    <span className="t1c-iv-note-ico" aria-hidden="true">
                      {n.tone === "ok" ? <Check /> : <Info />}
                    </span>
                    <div className="t1c-iv-note-txt">
                      <b>{n.title}</b>
                      {n.body && <span>{n.body}</span>}
                    </div>
                    <button type="button" className="t1c-iv-note-x" onClick={n.onDismiss} aria-label="Dismiss notice" data-tip="Dismiss" data-tip-pos="left">
                      <XIcon />
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}

          {isLoading && <div className="card pad mut sm">Loading your interview…</div>}
          {isError && (
            <div className="banner crit">
              <Info />
              <span>Couldn't load your interview. Please retry in a moment.</span>
            </div>
          )}

          {notStarted && !strict && (
            <div className="t1c-iv-start">
              <h2>Ready when you are</h2>
              <p className="mut">
                Asha — an AI interviewer, not a person — will ask each question out loud, and you answer by
                speaking. There's no trick — just talk through it in your own words.
              </p>
              <button className="btn primary t1c-cta" disabled={start.isPending} onClick={() => start.mutate()}>
                {start.isPending ? "Preparing questions…" : "Begin interview"} <Arrow />
              </button>
              {start.isError && (
                <div className="banner crit" style={{ marginTop: 12, textAlign: "left" }}>
                  <Info />
                  <span>Couldn't start the interview. Please retry in a moment.</span>
                </div>
              )}
            </div>
          )}

          {/* STRICT PRE-FLIGHT — both requirements green (or honestly waived) before the first
              question. Fullscreen needs a real click (requestFullscreen demands a user gesture),
              and every failure path here ends in a way forward, never a wall. */}
          {notStarted && strict && (() => {
            const cameraOk = cameraLive || bypassed.has("camera");
            const fullscreenOk = isFullscreen || agreedFullscreen || bypassed.has("fullscreen");
            return (
              <div className="t1c-iv-start">
                <h2>This interview runs in focus mode</h2>
                <p className="mut sm" style={{ marginTop: 0 }}>
                  The hiring team has asked for a distraction-checked session: your camera stays on and
                  the interview runs in fullscreen. If you leave fullscreen or the camera drops, the
                  interview simply pauses until it's fixed — it never ends on its own. Copy-paste is
                  off in the answer boxes, and while you're speaking an answer, attention to the screen
                  is noted on your own device. A person reviews everything; nothing is decided
                  automatically.
                </p>

                <div className="t1c-iv-req">
                  <span className={`t1c-iv-req-ico${cameraOk ? " ok" : ""}`}>
                    {cameraOk ? <Check /> : <Video />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>Camera</div>
                    {bypassed.has("camera") ? (
                      <div className="mut sm">
                        Continuing without a camera — noted as a technical limitation, not a mark against you.
                      </div>
                    ) : cameraLive ? (
                      <div className="mut sm">Your camera is on and we can see your video.</div>
                    ) : cam.denied || camLost ? (
                      <div>
                        <div className="mut sm">
                          {camLost
                            ? "Your camera stopped — it may be unplugged, or another app took it over."
                            : cameraReasonText(cam.reason)}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          <button className="btn sm ghost" onClick={cam.retry}>Try my camera again</button>
                          <button
                            className="btn sm ghost"
                            onClick={() => {
                              // Pre-flight escape hatch: a broken webcam must not dead-end an honest
                              // candidate. Buffered — proctoring isn't running yet — and flushed the
                              // moment it starts, so the report still learns about the waiver.
                              preStartBypassesRef.current.push("camera_unavailable");
                              setBypassed((prev) => new Set(prev).add("camera"));
                            }}
                          >
                            Continue without camera
                          </button>
                        </div>
                        <div className="mut sm" style={{ marginTop: 6 }}>
                          Continuing without it will be noted for the hiring team as a technical
                          limitation — it is not a mark against you.
                        </div>
                      </div>
                    ) : (
                      <div className="mut sm">Starting your camera…</div>
                    )}
                  </div>
                </div>

                <div className="t1c-iv-req">
                  <span className={`t1c-iv-req-ico${fullscreenOk ? " ok" : ""}`}>
                    {fullscreenOk ? <Check /> : <Eye />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>Fullscreen</div>
                    {bypassed.has("fullscreen") ? (
                      <div className="mut sm">
                        {fullscreenSupported
                          ? "Continuing without fullscreen — noted as a technical limitation, not a mark against you."
                          : "Your browser can't enter fullscreen — that's been noted as a technical limitation, not a mark against you."}
                      </div>
                    ) : isFullscreen ? (
                      <div className="mut sm">You're in fullscreen and ready to go.</div>
                    ) : (
                      <div>
                        <div className="mut sm">The interview runs fullscreen so nothing competes for your attention.</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          <label className="t1c-check">
                            <input
                              type="checkbox"
                              checked={agreedFullscreen}
                              onChange={(e) => setAgreedFullscreen(e.target.checked)}
                            />
                            <span>I agree to enter fullscreen mode</span>
                          </label>
                          {fsFailed && (
                            <button
                              className="btn sm ghost"
                              onClick={() => {
                                preStartBypassesRef.current.push("fullscreen_failed");
                                setBypassed((prev) => new Set(prev).add("fullscreen"));
                              }}
                            >
                              Continue without fullscreen
                            </button>
                          )}
                        </div>
                        {fsFailed && (
                          <div className="mut sm" style={{ marginTop: 6 }}>
                            Fullscreen didn't take in this browser. Continuing without it will be noted
                            as a technical limitation — it is not a mark against you.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  className="btn primary t1c-cta"
                  disabled={!cameraOk || !fullscreenOk || start.isPending}
                  onClick={() => {
                    if (agreedFullscreen && !isFullscreen && !bypassed.has("fullscreen")) {
                      enterFullscreen();
                    }
                    start.mutate();
                  }}
                >
                  {start.isPending ? "Preparing questions…" : "Begin interview"} <Arrow />
                </button>
                {start.isError && (
                  <div className="banner crit" style={{ marginTop: 12 }}>
                    <Info />
                    <span>Couldn't start the interview. Please retry in a moment.</span>
                  </div>
                )}
              </div>
            );
          })()}

          {!isLoading && !isError && progress.total > 0 && (
            <>
              {/* The bar and the counter track the PLAN. Adaptive follow-ups are real extra
                  questions, but burying them in the denominator is what made a 14-question
                  interview count up to 18 while the candidate watched — so they are disclosed
                  beside the count instead of hidden inside it. Done / current / upcoming each
                  have their own look. */}
              <div className="t1c-iv-progress">
                <ol className="t1c-iv-dots" aria-hidden="true">
                  {Array.from({ length: view.planned }).map((_, i) => {
                    const stepNum = i + 1;
                    const state = stepNum < view.position ? "done" : stepNum === view.position ? "now" : "todo";
                    return (
                      <li key={i} className={`t1c-iv-dot ${state}`}>
                        {state === "done" ? <Check /> : stepNum}
                      </li>
                    );
                  })}
                </ol>
                <div className="t1c-iv-count">
                  Question {view.position} of {view.planned}
                </div>
              </div>

              {question === null && (
                <div className="card pad mut sm" style={{ marginTop: 10 }}>
                  Wrapping up your interview…
                </div>
              )}

              {question !== null && (
                <>
                  {/* The question itself: a neutral card, large type, its kind as a small label.
                      A live challenge is the same card with its own label — never an amber alert. */}
                  <div className="t1c-iv-q">
                    <div className="t1c-iv-q-top">
                      <span className="pill t">
                        {question.kind === "adaptive_challenge"
                          ? "Live challenge"
                          : view.onFollowUp
                            ? "Follow-up"
                            : KIND_LABEL[question.kind] || "Introduction"}
                      </span>
                      <button
                        className="btn sm ghost"
                        disabled={!speech.supported || speech.speaking}
                        onClick={() => speech.speak(caption)}
                      >
                        <Repeat /> Replay
                      </button>
                    </div>

                    {/* Adaptive challenge keeps its live-twist framing (spoken too). */}
                    {question.kind === "adaptive_challenge" && (
                      <>
                        <h2 className="t1c-iv-q-text" aria-live="polite">
                          {spokenFor(question)}
                        </h2>
                        <p className="t1c-iv-q-note">
                          This live twist confirms it's really you doing the work — not something rehearsed or read aloud
                          by someone else.
                        </p>
                      </>
                    )}

                    {/* Revealed question text. */}
                    {showText && question.kind !== "adaptive_challenge" && (
                      <h2 className="t1c-iv-q-text" aria-live="polite">
                        {caption || spokenFor(question)}
                      </h2>
                    )}
                  </div>

                  {/* code_reading: the artefact to read out and critique. The heading is a neutral
                      label on purpose — the actual question is the prompt Asha speaks, which is
                      authored on the vertical's exercise and often carries the nudge that makes it
                      fair ("think about the empty list"). Hardcoding the question here would talk
                      over it, and would only ever be right for code. */}
                  {question.kind === "code_reading" && (
                    <div style={{ margin: "0 0 12px" }}>
                      <div className="mut sm" style={{ fontWeight: 600, marginBottom: 4 }}>
                        {choiceOptions(question)
                          ? "Read this, pick the statement you believe is right, then explain why"
                          : "Read this, then answer in your own words"}
                      </div>
                      {question.snippet ? (
                        <pre className="asha-snippet" aria-label="Snippet to read and explain">
                          {question.snippet}
                        </pre>
                      ) : (
                        <div className="card pad mut sm">The snippet is loading — tap Replay if you don't hear Asha.</div>
                      )}
                      {/* Snippet choice (#78): choose-then-explain. Plain <button>s, so keyboard
                          and screen-reader access come for free (Tab between, Enter/Space to
                          pick). The whole set disables once picked — the pick is locked, and the
                          candidate's job from that moment is the spoken why. No correctness is
                          ever shown here: candidates see no results mid-interview. */}
                      {(() => {
                        const opts = choiceOptions(question);
                        if (!opts) return null;
                        return (
                          <div role="group" aria-label="Pick the statement you believe is right" style={{ marginTop: 10 }}>
                            {opts.map((opt, i) => (
                              <button
                                key={i}
                                type="button"
                                className={`btn ghost t1c-iv-opt${picked === i ? " is-picked" : ""}`}
                                aria-pressed={picked === i}
                                disabled={picked !== null || answer.isPending}
                                onClick={() => pickOption(i)}
                              >
                                <b style={{ flexShrink: 0 }}>{OPTION_LETTERS[i] ?? i + 1}.</b>
                                <span>{opt}</span>
                              </button>
                            ))}
                            <div className="mut sm" aria-live="polite">
                              {picked === null
                                ? "Your pick locks in, and your spoken reasoning is what's assessed — take a moment before choosing."
                                : `You picked ${OPTION_LETTERS[picked] ?? picked + 1}. Now explain your reasoning out loud — that explanation is what's assessed.`}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {rephrase.isError && (
                    <div className="mut sm" style={{ marginBottom: 10 }}>
                      Couldn't simplify that just now — the original question still stands. Tap Replay to hear it again.
                    </div>
                  )}
                  {rephraseNote && (
                    <div className="mut sm" role="status" style={{ marginBottom: 10 }}>
                      {rephraseNote}
                    </div>
                  )}

                  {/* Answer surface */}
                  {question.kind === "code" && question.codeSpec && (
                    <div className="field">
                      <label>
                        Type your solution <span className="hint">· {question.codeSpec.language}</span>
                      </label>
                      <textarea
                        rows={9}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onPaste={answerPaste}
                        spellCheck={false}
                        style={{ fontFamily: "var(--mono)", fontSize: "12.5px", width: "100%" }}
                      />
                      <div className="mut sm" style={{ marginTop: 8, fontWeight: 600 }}>
                        Test cases — run on submit
                      </div>
                      {question.codeSpec.cases.map((c, i) => (
                        <div key={i} className="mut sm" style={{ fontFamily: "var(--mono)", padding: "3px 0" }}>
                          in: {c.stdin || "—"} → hidden test
                        </div>
                      ))}
                    </div>
                  )}
                  {!hasCodeEditor(question) && mode === "voice" && !micUnavailable && (
                    /* NO transcript shown (Hafiz + Cader, 17 Aug — QA #89). The live STT text
                       misspells names and accents and that unsettles candidates mid-answer, so
                       the words stay captured behind the scenes (submit sends React state, and
                       the recording-verified transcript is what scoring trusts anyway). What the
                       candidate needs instead is PROOF the answer is being captured — without
                       it, a Submit gated on transcript length looks broken. */
                    <div className="field">
                      <div className={`asha-capture t1c-iv-capture${micOpen ? " is-open" : ""}`} role="status" aria-live="polite">
                        <span className="t1c-iv-capture-dot" aria-hidden="true" />
                        <span style={{ flex: 1 }}>
                          {speech.speaking
                            ? "Asha is speaking… your mic switches on the moment she finishes."
                            : answer.isPending
                              ? "Sending your answer…"
                              : pageHidden
                                ? "Your mic is paused while this tab is in the background."
                                : micOpen
                                  ? voice.transcript.trim().length > 0
                                    ? "Listening — keep going, or submit when you're done."
                                    : "Listening… start speaking your answer."
                                  : "Just a moment…"}
                        </span>
                        {voice.transcript.trim().length > 0 && (
                          <span className="mut sm" style={{ flex: "none" }}>
                            Answer captured · {voice.transcript.trim().split(/\s+/).length} words
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mic unavailable → a fix-mic panel, never a typing box (owner decision, Oct 2026).
                      The candidate is never trapped: allow the mic and reload (the interview resumes
                      at this question — state lives on the server), or ask for a human interview. */}
                  {!hasCodeEditor(question) && micUnavailable && (
                    <div className="t1c-iv-micfix" role="alert">
                      <span className="t1c-iv-micfix-ico" aria-hidden="true"><MicOff size={20} /></span>
                      <div className="t1c-iv-micfix-body">
                        <h3>We can't hear you</h3>
                        {!voice.supported ? (
                          <p>
                            This browser can't capture spoken answers. Open your interview link in Chrome or Edge
                            — your progress is saved and you'll continue from this question.
                          </p>
                        ) : (
                          <>
                            <p>Your microphone is blocked or has disconnected. Your progress is saved.</p>
                            <ol>
                              <li>Click the lock or settings icon next to the address bar.</li>
                              <li>Set <b>Microphone</b> to <b>Allow</b> (and check your mic is plugged in).</li>
                              <li>Press <b>Try again</b> — you'll continue from this question.</li>
                            </ol>
                          </>
                        )}
                        <div className="t1c-iv-micfix-actions">
                          <button type="button" className="btn primary" onClick={() => window.location.reload()}>
                            <RotateCcw size={16} /> Try again
                          </button>
                          <RequestHuman token={token} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* One instruction, for the mode the candidate is actually in. */}
                  {(hasCodeEditor(question) || !micUnavailable) && (
                  <p className="t1c-iv-howto">
                    {hasCodeEditor(question)
                      ? "Write your solution above, then submit — the test cases run when you do."
                      : "Speak your answer out loud. When you've finished, press the button below. There's no time limit."}
                  </p>
                  )}

                  {answer.isError && (
                    <div className="banner crit" style={{ marginBottom: 12 }}>
                      <Info />
                      <span>Couldn't submit that answer. Your response is still here — please retry.</span>
                    </div>
                  )}

                  {/* nothing to send while the mic is down — the fix-mic panel is the only action then
                      (an answer already spoken before the mic dropped can still be submitted) */}
                  {(hasCodeEditor(question) || !micUnavailable || voice.transcript.trim().length > 0) && (
                    <button className="btn primary t1c-cta t1c-iv-submit" disabled={!canSubmit} onClick={submit}>
                      {submitLabel}
                      <ArrowRight size={18} />
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
